import type { StandardSchemaV1 } from '@standard-schema/spec'

import {
  infiniteQueryOptions,
  MutationMeta,
  type QueryKey,
  queryOptions,
} from '@repo/tanstack/query'
import { scopeUpFetch, UpFetch } from '@repo/upfetch'

// Infer input type from Standard Schema
type InferInput<T> = T extends StandardSchemaV1<infer I, unknown> ? I : never

// What a procedure's fn receives from its input, and the upFetch a request with that input uses.
type InputScope<TInput, TContext> = {
  context: (input: TInput) => TContext
  upFetch: (input: TInput) => UpFetch
}

// Options override types
type MutationOptionsOverride<TInput, TOutput> = {
  gcTime?: number
  meta?: MutationMeta
  onError?: (error: Error, variables: TInput, context: unknown) => void
  onMutate?: (variables: TInput) => Promise<unknown> | unknown
  onSettled?: (
    data: TOutput | undefined,
    error: Error | null,
    variables: TInput,
    context: unknown,
  ) => void
  onSuccess?: (data: TOutput, variables: TInput, context: unknown) => void
  retry?: boolean | number
  retryDelay?: ((attemptIndex: number) => number) | number
}

// Input fields that can name the Organisation a request is for.
type OrganisationKey<TInput> = keyof {
  [K in keyof TInput as TInput[K] extends number | undefined ? K : never]: K
}

// Segment types for path building
type PathSegment = number | string

type PathSegments = readonly PathSegment[]

// Function types for procedure callbacks - all receive path and upFetch
type QueryFn<TOutput> = (ctx: { path: string; upFetch: UpFetch }) => Promise<TOutput>

type QueryOptionsOverride = {
  enabled?: boolean
  gcTime?: number
  refetchInterval?: false | number
  refetchOnMount?: 'always' | boolean
  refetchOnReconnect?: 'always' | boolean
  refetchOnWindowFocus?: 'always' | boolean
  retry?: boolean | number
  retryDelay?: ((attemptIndex: number) => number) | number
  staleTime?: number
}

type WithInput<TContext> = TContext & { path: string; upFetch: UpFetch }

export function createProcedure(upFetch: UpFetch) {
  // Overloaded path function
  // 1. Static path: path('loads', 'comments') → '/loads/comments'
  function path(...segments: PathSegments): ReturnType<typeof createStaticPath>
  // 2. Parameterized path: path(schema, p => ['loads', p.id]) → '/loads/123'
  function path<TSchema extends StandardSchemaV1>(
    schema: TSchema,
    builder: (params: InferInput<TSchema>) => PathSegments,
  ): ReturnType<typeof createParamPathChain<TSchema>>
  function path<TSchema extends StandardSchemaV1>(
    schemaOrFirstSegment: PathSegment | TSchema,
    builderOrSecondSegment?: ((params: InferInput<TSchema>) => PathSegments) | PathSegment,
    ...restSegments: PathSegments
  ) {
    // Check if first arg is a schema (has ~standard property)
    if (
      typeof schemaOrFirstSegment === 'object' &&
      schemaOrFirstSegment !== null &&
      '~standard' in schemaOrFirstSegment
    ) {
      // Parameterized path: path(schema, builder)
      // SAFETY: the only overload that takes a schema first requires the path builder second.
      return createParamPathChain(
        upFetch,
        schemaOrFirstSegment,
        builderOrSecondSegment as (params: InferInput<TSchema>) => PathSegments,
      )
    }
    // Static path: path(...segments)
    const segments = [schemaOrFirstSegment, builderOrSecondSegment, ...restSegments].filter(
      (s): s is PathSegment => s !== undefined,
    )
    return createStaticPath(upFetch, segments)
  }

  return { path }
}

const createStaticPath = (upFetch: UpFetch, segments: PathSegments) => ({
  ...createStaticPathChain(upFetch, segments, segments),
  // Keys the procedure by `keySegments` rather than its path, so a second procedure requesting
  // the same path never shares its keys or matches its filter prefixes.
  key: (...keySegments: PathSegments) => createStaticPathChain(upFetch, segments, keySegments),
})

function createParamPathChain<TSchema extends StandardSchemaV1>(
  upFetch: UpFetch,
  pathSchema: TSchema,
  builder: (params: InferInput<TSchema>) => PathSegments,
) {
  type TPath = InferInput<TSchema>

  // Helper to build path string from segments
  const buildPath = (pathParams: TPath) => '/' + builder(pathParams).join('/')

  return {
    input<TInputSchema extends StandardSchemaV1>(inputSchema: TInputSchema) {
      type TInput = InferInput<TInputSchema>

      type TArgs = { input: TInput; path: TPath }

      const chain = <TContext>(scope: InputScope<TInput, TContext>) => {
        const resolve = async (args: TArgs) => ({
          context: scope.context(await validateInput(inputSchema, args.input)),
          path: buildPath(await validateInput(pathSchema, args.path)),
        })
        const allScopesKey = (args: TArgs) => [...builder(args.path), args.input] as const
        const queryKey = (upFetch: UpFetch, args: TArgs) => scopedKey(upFetch, allScopesKey(args))

        return {
          infiniteQuery<TOutput>(
            fn: (ctx: WithInput<TContext> & { page: number }) => Promise<TOutput>,
            getNextPageParam: (lastPage: TOutput) => number | undefined,
          ) {
            return {
              allScopesKey,
              queryKey: (args: TArgs) => infiniteKey(queryKey(scope.upFetch(args.input), args)),
              queryOptions: (args: TArgs, options?: QueryOptionsOverride) => {
                const upFetch = scope.upFetch(args.input)
                return infiniteQueryOptions({
                  getNextPageParam,
                  initialPageParam: 1,
                  queryFn: async ({ pageParam }) => {
                    const { context, path } = await resolve(args)
                    return fn({ ...context, page: pageParam, path, upFetch })
                  },
                  queryKey: infiniteKey(queryKey(upFetch, args)),
                  ...options,
                })
              },
            }
          },
          mutation<TOutput = unknown>(fn: (ctx: WithInput<TContext>) => Promise<TOutput>) {
            const mutate = async (args: TArgs) => {
              const { context, path } = await resolve(args)
              return fn({ ...context, path, upFetch: scope.upFetch(args.input) })
            }

            return {
              mutate,
              mutationOptions: (
                args: { path: TPath },
                options?: MutationOptionsOverride<TInput, TOutput>,
              ) => ({
                mutationFn: async (input: TInput) => mutate({ input, path: args.path }),
                ...options,
              }),
              queryKey: (args: { path: TPath }) =>
                scopedKey(upFetch, [...builder(args.path)] as const),
            }
          },
          query<TOutput>(fn: (ctx: WithInput<TContext>) => Promise<TOutput>) {
            const query = async (args: TArgs, upFetch: UpFetch) => {
              const { context, path } = await resolve(args)
              return fn({ ...context, path, upFetch })
            }

            return {
              allScopesKey,
              query: async (args: TArgs) => query(args, scope.upFetch(args.input)),
              queryKey: (args: TArgs) => queryKey(scope.upFetch(args.input), args),
              queryOptions: (args: TArgs, options?: QueryOptionsOverride) => {
                const upFetch = scope.upFetch(args.input)
                return queryOptions({
                  queryFn: async () => query(args, upFetch),
                  queryKey: queryKey(upFetch, args),
                  ...options,
                })
              },
            }
          },
        }
      }

      return {
        ...chain(unscoped<TInput>(upFetch)),
        organisation: <K extends OrganisationKey<TInput>>(key: K) => chain(scopedBy(upFetch, key)),
      }
    },

    mutation<TOutput = unknown>(fn: QueryFn<TOutput>) {
      return {
        mutate: async (args: { path: TPath }) => {
          const validated = await validateInput(pathSchema, args.path)
          const path = buildPath(validated)
          return fn({ path, upFetch })
        },
        mutationOptions: (
          args: { path: TPath },
          options?: MutationOptionsOverride<void, TOutput>,
        ) => ({
          mutationFn: async () => {
            const validated = await validateInput(pathSchema, args.path)
            const path = buildPath(validated)
            return fn({ path, upFetch })
          },
          ...options,
        }),
        queryKey: (args: { path: TPath }) => scopedKey(upFetch, [...builder(args.path)] as const),
      }
    },

    query<TOutput>(fn: QueryFn<TOutput>) {
      return {
        allScopesKey: (args: { path: TPath }) => [...builder(args.path)] as const,
        query: async (args: { path: TPath }) => {
          const validated = await validateInput(pathSchema, args.path)
          const path = buildPath(validated)
          return fn({ path, upFetch })
        },
        queryKey: (args: { path: TPath }) => scopedKey(upFetch, [...builder(args.path)] as const),
        queryOptions: (args: { path: TPath }, options?: QueryOptionsOverride) =>
          queryOptions({
            queryFn: async () => {
              const validated = await validateInput(pathSchema, args.path)
              const path = buildPath(validated)
              return fn({ path, upFetch })
            },
            queryKey: scopedKey(upFetch, [...builder(args.path)]),
            ...options,
          }),
      }
    },
  }
}

function createStaticPathChain(
  upFetch: UpFetch,
  segments: PathSegments,
  keySegments: PathSegments,
) {
  const path = '/' + segments.join('/')

  return {
    infiniteQuery<TOutput>(
      fn: (ctx: { page: number; path: string; upFetch: UpFetch }) => Promise<TOutput>,
      getNextPageParam: (lastPage: TOutput) => number | undefined,
    ) {
      const queryKey = infiniteKey(scopedKey(upFetch, [...keySegments]))

      return {
        allScopesKey: () => [...keySegments] as const,
        queryKey: () => queryKey,
        queryOptions: (options?: QueryOptionsOverride) =>
          infiniteQueryOptions({
            getNextPageParam,
            initialPageParam: 1,
            queryFn: async ({ pageParam }) => fn({ page: pageParam, path, upFetch }),
            queryKey,
            ...options,
          }),
      }
    },

    input<TSchema extends StandardSchemaV1>(inputSchema: TSchema) {
      type TInput = InferInput<TSchema>

      type TArgs = { input: TInput }

      const chain = <TContext>(scope: InputScope<TInput, TContext>) => {
        const resolve = async (args: TArgs) =>
          scope.context(await validateInput(inputSchema, args.input))
        const allScopesKey = (args: TArgs) => [...keySegments, args.input] as const
        const queryKey = (upFetch: UpFetch, args: TArgs) => scopedKey(upFetch, allScopesKey(args))

        return {
          infiniteQuery<TOutput>(
            fn: (ctx: WithInput<TContext> & { page: number }) => Promise<TOutput>,
            getNextPageParam: (lastPage: TOutput) => number | undefined,
          ) {
            return {
              allScopesKey,
              queryKey: (args: TArgs) => infiniteKey(queryKey(scope.upFetch(args.input), args)),
              queryOptions: (args: TArgs, options?: QueryOptionsOverride) => {
                const upFetch = scope.upFetch(args.input)
                return infiniteQueryOptions({
                  getNextPageParam,
                  initialPageParam: 1,
                  queryFn: async ({ pageParam }) =>
                    fn({ ...(await resolve(args)), page: pageParam, path, upFetch }),
                  queryKey: infiniteKey(queryKey(upFetch, args)),
                  ...options,
                })
              },
            }
          },
          mutation<TOutput = unknown>(fn: (ctx: WithInput<TContext>) => Promise<TOutput>) {
            const mutate = async (args: TArgs) =>
              fn({ ...(await resolve(args)), path, upFetch: scope.upFetch(args.input) })

            return {
              mutate,
              mutationOptions: (options?: MutationOptionsOverride<TInput, TOutput>) => ({
                mutationFn: async (input: TInput) => mutate({ input }),
                ...options,
              }),
              queryKey: () => scopedKey(upFetch, [...keySegments] as const),
            }
          },
          query<TOutput>(fn: (ctx: WithInput<TContext>) => Promise<TOutput>) {
            const query = async (args: TArgs, upFetch: UpFetch) =>
              fn({ ...(await resolve(args)), path, upFetch })

            return {
              allScopesKey,
              query: async (args: TArgs) => query(args, scope.upFetch(args.input)),
              queryKey: (args: TArgs) => queryKey(scope.upFetch(args.input), args),
              queryOptions: (args: TArgs, options?: QueryOptionsOverride) => {
                const upFetch = scope.upFetch(args.input)
                return queryOptions({
                  queryFn: async () => query(args, upFetch),
                  queryKey: queryKey(upFetch, args),
                  ...options,
                })
              },
            }
          },
        }
      }

      return {
        ...chain(unscoped<TInput>(upFetch)),
        organisation: <K extends OrganisationKey<TInput>>(key: K) => chain(scopedBy(upFetch, key)),
      }
    },

    mutation<TOutput = unknown>(fn: QueryFn<TOutput>) {
      return {
        mutate: async () => fn({ path, upFetch }),
        mutationOptions: (options?: MutationOptionsOverride<void, TOutput>) => ({
          mutationFn: async () => fn({ path, upFetch }),
          ...options,
        }),
        queryKey: () => scopedKey(upFetch, [...keySegments] as const),
      }
    },

    query<TOutput>(fn: QueryFn<TOutput>) {
      return {
        allScopesKey: () => [...keySegments] as const,
        query: async () => fn({ path, upFetch }),
        queryKey: () => scopedKey(upFetch, [...keySegments] as const),
        queryOptions: (options?: QueryOptionsOverride) =>
          queryOptions({
            queryFn: async () => fn({ path, upFetch }),
            queryKey: scopedKey(upFetch, [...keySegments]),
            ...options,
          }),
      }
    },
  }
}

// A query's key is its path (or the segments `key` names) and input, then the Organisation scope's
// suffix. `allScopesKey` is the key without that suffix: a filter prefix matching the query in
// every scope, for a change that reaches every scope's cache. Without input it is the path alone,
// so it also matches the queries of longer paths under it.
// An infinite query's key is the query key of the same input plus 'infinite', so the two never
// share a cache entry (their data differ) and either's `allScopesKey` covers both.
const infiniteKey = (queryKey: QueryKey): QueryKey => [...queryKey, 'infinite']

// The key of a query sent through `upFetch`: `queryKey` and then its Organisation scope's suffix.
const scopedKey = <TQueryKey extends QueryKey>(upFetch: UpFetch, queryKey: TQueryKey) =>
  [...queryKey, ...upFetch.getQueryKeyContext()] as const

const unscoped = <TInput>(upFetch: UpFetch): InputScope<TInput, { input: TInput }> => ({
  context: input => ({ input }),
  upFetch: () => upFetch,
})

// Scopes each request to the Organisation in its `key` field, which the fn then receives as
// `organisationId` rather than in `input`. An undefined value keeps the provider's scope.
const scopedBy = <TInput, K extends OrganisationKey<TInput>>(
  upFetch: UpFetch,
  key: K,
): InputScope<TInput, { input: Omit<TInput, K>; organisationId: TInput[K] }> => ({
  context: ({ [key]: organisationId, ...input }) => ({ input, organisationId }),
  upFetch: input => {
    // SAFETY: OrganisationKey only admits keys whose value is a number or undefined.
    const id = input[key] as number | undefined
    return id === undefined ? upFetch : scopeUpFetch(upFetch, { id, kind: 'organisation' })
  },
})

// Validate input using Standard Schema
async function validateInput<TInput>(schema: StandardSchemaV1, input: TInput): Promise<TInput> {
  const result = await schema['~standard'].validate(input)
  if (result.issues) {
    throw new Error(`Input validation failed: ${result.issues.map(i => i.message).join(', ')}`)
  }
  return result.value as TInput
}
